import React, { FC, useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Grid, Theme } from '@mui/material'

import { endOfDay, startOfDay } from 'date-fns'
import { FormProvider, useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom'
import { toast } from 'react-toastify'
import { columns } from './tradesList'
import { DataPicker } from '../../../components/DatePicker'
import { Search } from '../../../components/Search'
import { TableComponent } from '../../../components/Table'
import { FilterSelect } from '../../../components/FilterSelect'
import { useRest } from '../../../components/Context'
import useDebounce from '../../../hooks/useDebounce'
import { generateParams } from '../../../utils'
import { routes } from '../../../constants/routes'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(4),
  },
  rightSide: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 'max-content',
  },
  title: {
    lineHeight: 0,
  },
  button: {
    paddingLeft: 15,
  },
  table: {
    paddingTop: theme.spacing(1),
  },
}))

interface UserTradesProps {
  userId?: string | undefined
}

export const UserTrades: FC<UserTradesProps> = ({ userId }) => {
  const classes = useStyles()
  const history = useHistory()
  const { rxios } = useRest()
  const isCompanyRoute = !!useRouteMatch(routes.COMPANIES)

  const formMethods = useForm({
    mode: 'onBlur',
  })
  const { watch, getValues } = formMethods

  // eslint-disable-next-line
  const [startDateSelected, setStartDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [endDateSelected, setEndDateSelected] = useState<any>('')
  // eslint-disable-next-line
  const [pair, setPair] = useState<any>('')
  // eslint-disable-next-line
  const [pairsSelect, setPairsSelect] = useState<any>('')
  const [queryString, setQueryString] = useState<string>('')
  const [searchDebounce, setSearchDebounce] = useState<string>('')
  const debouncedValue = useDebounce<string>(queryString, 200)
  // eslint-disable-next-line
  const [loading, setLoading] = useState<boolean>(false)
  const [isInitDate, setInitDate] = useState<boolean>(false)
  // eslint-disable-next-line
  const [values, setValues] = useState<any>({})
  const { tab } = useParams<{ tab: string }>()

  const { search } = useLocation()
  const stringParams = new URLSearchParams(search)
  const pageUrl: number | string | null = stringParams.get('page')
  // eslint-disable-next-line
  useEffect(() => setSearchDebounce(queryString), [debouncedValue])

  const pushParamsToURL = useCallback(
    (startDate, endDate, pairs, searchString) => {
      const currentUrlParams = new URLSearchParams(window.location.search)

      if (startDate) {
        currentUrlParams.set('startDate', startDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('startDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (endDate) {
        currentUrlParams.set('endDate', endDate)
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      } else {
        currentUrlParams.delete('endDate')
        history.push(`${window.location.pathname}?${currentUrlParams}`)
      }

      if (pairs) {
        currentUrlParams.set('pair', pairs.toUpperCase())
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('pair')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }

      if (searchString) {
        currentUrlParams.set('search', searchString)
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      } else {
        currentUrlParams.delete('search')
        history.push(`${window.location.pathname}?${currentUrlParams.toString()}`)
      }
    },
    [history],
  )

  const loadData = useCallback(
    async (page, startDate = '', endDate = '', pairs = '', searchString = '') => {
      let cleanupFunction = false
      setLoading(true)

      rxios
        .get(
          `trading/trades-by-user/${userId}?${generateParams({
            page,
            count: '10',
            startDate,
            endDate,
            pair: pairs.toUpperCase(),
            search: searchString.toUpperCase(),
            ...(isCompanyRoute && { companyId: userId }),
          })}`,
        )
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setValues(next)
            history.push(`${tab}?page=${page}`)
            pushParamsToURL(startDate, endDate, pairs, searchString)

            if (!cleanupFunction) setLoading(false)
          },
          (error: Error) => {
            pushParamsToURL(startDate, endDate, pairs, searchString)
            toast.error(error)
            setLoading(false)
          },
          () => {
            setLoading(false)
          },
        )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [history, rxios],
  )

  const initData = useCallback(
    async (page) => {
      let cleanupFunction = false
      setLoading(true)

      rxios
        .get(
          `trading/trades-by-user/${userId}?${generateParams({
            page,
            count: '10',
            ...(isCompanyRoute && { companyId: userId }),
          })}`,
        )
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setValues(next)
            if (!cleanupFunction) setLoading(false)
            setInitDate(true)

            history.push(`${tab}?page=${page}`)
          },
          (error: Error) => {
            toast.error(error)
            setLoading(false)
          },
          () => {
            setLoading(false)
          },
        )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [rxios],
  )

  const loadPairsList = useCallback(async () => {
    setLoading(true)

    rxios.get(`trading/pairs-by-${isCompanyRoute ? 'company' : 'user'}/${userId}`).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        const pairs = []

        // eslint-disable-next-line no-restricted-syntax
        for (const name of next) {
          pairs.push({
            value: name,
            label: name,
          })
        }
        setPairsSelect(pairs)
      },
      (error) => {
        toast.error(error)
      },
    )
    // eslint-disable-next-line
  }, [rxios])

  useEffect(() => {
    ;(async () => {
      await initData(pageUrl || '1')
      await loadPairsList()
    })()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'startDate') setStartDateSelected(value?.startDate?.toISOString())
      if (name === 'endDate') setEndDateSelected(value?.endDate?.toISOString())
      if (name === 'pair') setPair(value?.pair.toLowerCase().replaceAll(' ', '_'))
      if (name === 'search') setQueryString(value?.[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    if (isInitDate) {
      setEndDateSelected(endDateSelected === '1970-01-01' ? '' : endDateSelected)
      ;(async () => {
        await loadData(1, startDateSelected || '', endDateSelected || '', pair || '', searchDebounce || '')
      })()
    }
    // eslint-disable-next-line
  }, [startDateSelected, endDateSelected, pair, searchDebounce])

  return (
    <FormProvider {...formMethods}>
      <Grid container className={classes.topWrap}>
        <Grid container className={classes.rightSide}>
          <DataPicker
            placeholder={'Start Date'}
            margin="8px 8px 8px 0"
            dataPickerName={'startDate'}
            required={false}
            maxDate={getValues().endDate}
            formatValue={startOfDay}
          />
          <DataPicker
            placeholder={'End Date'}
            margin={8}
            dataPickerName={'endDate'}
            required={false}
            minDate={getValues().startDate}
            formatValue={endOfDay}
          />
          <FilterSelect inputName={'pair'} placeholder="Pair" width={138} ml={8} mr={8} enums={pairsSelect} />
        </Grid>
        <Search inputName={'search'} pr={0} pl={0} />
      </Grid>

      <Grid container className={classes.table}>
        <TableComponent
          columns={columns}
          data={values.items}
          params={values.meta}
          loading={loading}
          loadData={(pageNumber: number | string | null) =>
            loadData(pageNumber, startDateSelected || '', endDateSelected || '', pair || '', searchDebounce || '')
          }
        />
      </Grid>
    </FormProvider>
  )
}
